
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react"
import { useNavigate } from "react-router-dom"
import {
  login as apiLogin,
  register as apiRegister,
  logout as apiLogout,
  type User,
} from "@/lib/api-client"

interface AuthContextValue {
  user: User | null
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (body: {
    email: string
    password: string
    name: string
    company_name?: string
    company_stage?: string
    industry?: string
  }) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()

  // On mount, try to restore user from cookie by checking if a token exists
  useEffect(() => {
    const hasToken =
      typeof document !== "undefined" &&
      document.cookie.includes("irene_token=")
    if (hasToken) {
      // We have a token, try to fetch metrics as a health check
      // If it works, the user is authenticated
      const savedUser = typeof window !== "undefined"
        ? window.sessionStorage.getItem("irene_user")
        : null
      if (savedUser) {
        try {
          setUser(JSON.parse(savedUser))
        } catch {
          // ignore
        }
      }
    }
    setIsLoading(false)
  }, [])

  const login = useCallback(
    async (email: string, password: string) => {
      const { user: u } = await apiLogin(email, password)
      setUser(u)
      if (typeof window !== "undefined") {
        window.sessionStorage.setItem("irene_user", JSON.stringify(u))
      }
    },
    []
  )

  const register = useCallback(
    async (body: {
      email: string
      password: string
      name: string
      company_name?: string
      company_stage?: string
      industry?: string
    }) => {
      const { user: u } = await apiRegister(body)
      setUser(u)
      if (typeof window !== "undefined") {
        window.sessionStorage.setItem("irene_user", JSON.stringify(u))
      }
    },
    []
  )

  const logout = useCallback(async () => {
    await apiLogout()
    setUser(null)
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem("irene_user")
    }
    navigate("/login")
  }, [navigate])

  return (
    <AuthContext.Provider value={{ user, isLoading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return ctx
}
